import { useEffect, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { sanitizeReturnTo, useLocalAuth } from '@wehunt/core-web'
import { COLORS, Icon, AuthShell, AuthHeader, SsoButton, Divider, AppleIcon, GoogleIcon, authInputStyle } from '@wehunt/ui'

// New login design (login redesign): WeHunt-branded sign-in card with Apple/Google
// SSO, email + password (show/hide), remember-me and forgot-password, and a link to
// create an account. Token-themed (dark mode) and i18n-driven.
export function SignInPage() {
  const { signIn, signInWithGoogle, signInWithApple, sendPasswordReset, setRememberMe, state } = useLocalAuth()
  const { t } = useTranslation('app')
  const navigate = useNavigate()
  const { returnTo } = useSearch({ strict: false }) as { returnTo?: string }
  // Kept on the way to the other auth page, so a shared link opened while signed
  // out survives sign-in or sign-up, whichever the visitor picks.
  const authSearch = returnTo === undefined ? {} : { returnTo }

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [remember, setRemember] = useState(true)
  const [busy, setBusy] = useState<null | 'email' | 'google' | 'apple'>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    // Where the visitor was heading before the guard sent them here. The value
    // came in on the URL, so it is sanitised to an on-origin path first: an
    // absolute or protocol-relative returnTo would make this an open redirect.
    if (state.status === 'signed-in') void navigate({ href: sanitizeReturnTo(returnTo) })
  }, [state.status])

  const mapError = (err: unknown): string => {
    const code = (err as { code?: string })?.code
    if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
      return t('invalidCredentials')
    }
    return err instanceof Error && err.message ? err.message : t('genericError')
  }

  const run = async (kind: 'email' | 'google' | 'apple', fn: () => Promise<void>) => {
    setError(null); setNotice(null); setBusy(kind)
    try {
      // Honour "remember me" before any sign-in so persistence is set correctly.
      await setRememberMe(remember)
      await fn()
    } catch (err) {
      setError(mapError(err))
    } finally {
      setBusy(null)
    }
  }

  const onEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void run('email', () => signIn(email, password))
  }

  const onForgot = async () => {
    setError(null); setNotice(null)
    if (!email.trim()) { setNotice(t('signIn.resetNeedEmail')); return }
    try {
      await sendPasswordReset(email.trim())
      setNotice(t('signIn.resetSent', { email: email.trim() }))
    } catch (err) {
      setError(mapError(err))
    }
  }

  return (
    <AuthShell>
      {/* The app icon stands in for the old "WeHunt · Sign in" eyebrow. It is the
          same mark the PWA and apple-touch icons use, so the login screen, the
          browser tab and the installed icon all read as one product. */}
      <AuthHeader
        logoSrc="/assets/icon-192.png"
        logoAlt="WeHunt"
        align="center"
        title={t('signIn.title')}
        subtitle={t('signIn.subtitle')}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SsoButton onClick={() => run('apple', signInWithApple)} disabled={busy !== null} icon={<AppleIcon />}>
          {t('signIn.continueWithApple')}
        </SsoButton>
        <SsoButton onClick={() => run('google', signInWithGoogle)} disabled={busy !== null} icon={<GoogleIcon />}>
          {t('signIn.continueWithGoogle')}
        </SsoButton>
      </div>

      <Divider>{t('signIn.orWithEmail')}</Divider>

      <form onSubmit={onEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('email')}
          aria-label={t('email')}
          autoComplete="email"
          style={authInputStyle}
        />
        <div style={{ position: 'relative' }}>
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('password')}
            aria-label={t('password')}
            autoComplete="current-password"
            style={{ ...authInputStyle, paddingRight: 46 }}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? t('signIn.hidePassword') : t('signIn.showPassword')}
            style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              width: 34, height: 34, border: 'none', background: 'transparent',
              color: COLORS.outline, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Icon name={showPassword ? 'eye-off' : 'eye'} size={20} />
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '2px 0 4px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: COLORS.onSurface, cursor: 'pointer' }}>
            <input type="checkbox" className="wh-checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            {t('signIn.rememberMe')}
          </label>
          <button type="button" onClick={onForgot} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: COLORS.onSurface }}>
            {t('signIn.forgotPassword')}
          </button>
        </div>

        {error && <p role="alert" style={{ margin: 0, color: COLORS.error, fontSize: 14 }}>{error}</p>}
        {notice && <p role="status" style={{ margin: 0, color: COLORS.primary, fontSize: 14 }}>{notice}</p>}

        <button
          type="submit"
          className="wh-btn wh-btn-primary"
          disabled={busy !== null}
          style={{ height: 52, borderRadius: 12, fontSize: 16, fontWeight: 700, marginTop: 4 }}
        >
          {busy === 'email' ? t('signingIn') : t('submit')}
          <Icon name="arrow-right" size={18} />
        </button>
      </form>

      <div style={{ marginTop: 22, paddingTop: 20, borderTop: `1px solid ${COLORS.outlineVariant}`, textAlign: 'center', fontSize: 14, color: COLORS.onSurfaceVariant }}>
        {t('signIn.newHere')}{' '}
        <button type="button" onClick={() => navigate({ to: '/sign-up', search: authSearch })} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: COLORS.primary }}>
          {t('signIn.createAccount')}
        </button>
      </div>
    </AuthShell>
  )
}
